<script setup>
import axios from "axios";
import { reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";

import clearObject from "@/utils/clearObject";
import useGTM from "@/utils/gtm";
import { validateEmail, validateName } from "@/utils/validation";

const { t, tm } = useI18n();
const emit = defineEmits(["closeDialog"]);

const { name, email, withTitle } = defineProps({
  name: {
    type: String,
    default: "",
  },
  email: {
    type: String,
    default: "",
  },
  withTitle: {
    type: Boolean,
    default: true,
  },
});

const formData = reactive({
  name: name,
  email: email,
  subject: "",
  message: "",
});

const errors = reactive({
  name: "",
  email: "",
  subject: "",
  message: "",
});

const isValid = () => {
  errors.name = validateName(formData.name);
  errors.email = validateEmail(formData.email);
  errors.subject = validateName(formData.subject);
  errors.message = validateName(formData.message);
  return Object.values(errors).every((error) => error === "");
};

const sendMessage = () => {
  if (isValid()) {
    try {
      axios.post("/api/contact-form", {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
      emit("closeDialog");
      useToast().success(t("contact_form.message_sent_successfully"));

      useGTM.pushEvent({
        event: "form_submit",
      });
      clearObject(formData, "");
      clearObject(errors, "");
    } catch (error) {
      useToast().error(t("notifications.default_error"));
      console.error(error);
    }
  }
};
</script>
<template>
  <div class="contact-form-container">
    <form @submit.prevent="sendMessage" class="contact-form">
      <slot name="close" />
      
      <h2 v-if="withTitle" class="form-title">
        {{ t("contact_form.contact_support") }}
      </h2>
      
      <div class="form-content">
        <!-- Name Field -->
        <div class="form-field">
          <label class="form-label">{{ t('contact_form.name') }}</label>
          <input
            v-model="formData.name"
            type="text"
            class="form-input"
            :class="{ error: errors.name }"
            :placeholder="t('contact_form.name')"
            required
          />
          <span v-if="errors.name" class="error-text">{{ errors.name }}</span>
        </div>

        <!-- Email Field -->
        <div class="form-field">
          <label class="form-label">{{ t('contact_form.email') }}</label>
          <input
            v-model="formData.email"
            type="email"
            class="form-input"
            :class="{ error: errors.email }"
            :placeholder="t('contact_form.email')"
            required
          />
          <span v-if="errors.email" class="error-text">{{ errors.email }}</span>
        </div>

        <!-- Subject Field -->
        <div class="form-field">
          <label class="form-label">{{ t('contact_form.subject') }}</label>
          <select
            v-model="formData.subject"
            class="form-input"
            :class="{ error: errors.subject }"
            required
          >
            <option value="" disabled>{{ t('contact_form.subject') }}</option>
            <option
              v-for="subject in tm('contact_form.subject_types')"
              :key="subject"
              :value="subject"
            >
              {{ subject }}
            </option>
          </select>
          <span v-if="errors.subject" class="error-text">{{ errors.subject }}</span>
        </div>

        <!-- Message Field -->
        <div class="form-field">
          <label class="form-label">{{ t('contact_form.describe_your_problem') }}</label>
          <textarea
            v-model="formData.message"
            class="form-input form-textarea"
            :class="{ error: errors.message }"
            :placeholder="t('contact_form.describe_your_problem')"
            rows="4"
            required
          ></textarea>
          <span v-if="errors.message" class="error-text">{{ errors.message }}</span>
        </div>

        <!-- Submit Button -->
        <button type="submit" class="submit-btn">
          {{ t('contact_form.send') }}
        </button>

        <!-- Contact Image -->
        <div v-if="withTitle" class="contact-image">
          <img class="contact-icon" src="/src/assets/images/contact.png" alt="Contact form" />
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped lang="scss">
.contact-form-container {
  background-color: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  margin: 0 auto;
}

.contact-form {
  padding: 48px 32px 32px 32px;
}

.form-title {
  text-align: center;
  font-size: 32px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  line-height: 1.2;
}

.form-content {
  margin-top: 24px;
}

.form-field {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 16px 16px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  font-size: 16px;
  transition: border-color 0.2s;
  background-color: white;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: none;
  }

  &.error {
    border-color: #ef4444;
  }

  &::placeholder {
    color: #9ca3af;
  }

}

// Select specific styling
select.form-input {
  cursor: pointer;
  appearance: none;
  background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>');
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 12px;
  padding-right: 40px;
}

.form-textarea {
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
}

.error-text {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.submit-btn {
  width: 100%;
  background-color: #d8ed84;
  color: #1f2937;
  border: none;
  border-radius: 16px;
  padding: 16px 14px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 24px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #c9e16f;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: rgba(216, 237, 132, 0.4);
    color: rgba(31, 41, 55, 0.6);
    cursor: not-allowed;
    box-shadow: 0 0 0 1px rgba(216, 237, 132, 0.2);
  }
}

.contact-image {
  text-align: center;
  margin-top: 16px;
}

.contact-icon {
  max-width: 100%;
  height: auto;
  max-height: 250px;
  border-radius: 12px;
  opacity: 0.8;
}

@media (max-width: 768px) {
  .contact-form-container {
    max-height: 100vh;
    min-height: 100vh;
    height: 100vh;
    margin: 0;
    box-shadow: none;
    display: flex;
    flex-direction: column;
  }
  
  .contact-form {
    padding: 48px 20px 24px 20px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  
  .form-content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  
  .contact-image {
    margin-top: auto;
  }

  .form-title {
    font-size: 24px;
  }

  .form-input {
    font-size: 16px;
    padding: 14px 16px;
  }

  .submit-btn {
    padding: 14px 16px;
    font-size: 16px;
  }

  .contact-icon {
    max-height: 150px;
  }
}

@media (max-width: 480px) {
  .contact-form {
    padding: 48px 16px 20px 16px;
  }

  .form-title {
    font-size: 20px;
  }

  .contact-icon {
    max-height: 120px;
  }
}
</style>
